// Times as a person reads them (R10-Q6): in their own zone (Preferences, else the company's;
// /me's `effective_timezone`), 24-hour, never a numeric month-first date (R10-S14: a
// Vietnamese team reads 10/01 as January). The shell sets the zone once /me is known; the
// words follow the interface language (R10-Q16: dates in vi-VN). Dates too (R10-B2): a
// moment's day is the day in that zone, and a calendar day ("2026-09-28": a due date, a range)
// is written as it is, never shifted by a zone.

import { dateLocale, t } from "../i18n";

let zone: string | undefined;
let locale = "";
const formats = new Map<string, Intl.DateTimeFormat>();

/** The zone times are shown in; undefined = this browser's (before /me loads, in tests). */
export function setTimeZone(next: string | null | undefined): void {
  const z = next || undefined;
  if (z === zone) return;
  zone = z;
  formats.clear();
}

/** The zone dates are read in ("" = this browser's): a cache of days keys on it. */
export function timeZone(): string {
  return zone ?? "";
}

function format(key: string, opts: Intl.DateTimeFormatOptions, loc?: string): Intl.DateTimeFormat {
  // The interface language changed: the formats are made again in its locale.
  if (locale !== dateLocale()) {
    locale = dateLocale();
    formats.clear();
  }
  const id = loc ? `${loc}|${key}` : key;
  let f = formats.get(id);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(loc ?? locale, { ...opts, timeZone: zone });
    } catch {
      f = new Intl.DateTimeFormat(loc ?? locale, opts); // an unknown zone: the browser's
    }
    formats.set(id, f);
  }
  return f;
}

/** 24-hour time ("10:32"), as the mockup shows message times. */
export function clock(iso: string | number | Date): string {
  return format("clock", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(
    new Date(iso),
  );
}

/** "Monday 5 October 2026 · 09:12": what tapping a time shows (R10-Q6). */
export function fullDate(iso: string): string {
  const day = formatDay(dayKey(iso), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return `${day.replace(",", "")} · ${clock(iso)}`;
}

/** "Mon 5 Oct 2026 · 09:12": a moment in a list (devices, runs, history). */
export function dateTime(iso: string): string {
  const day = formatDay(dayKey(iso), {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${day.replace(",", "")} · ${clock(iso)}`;
}

// ---- days -----------------------------------------------------------------------------------

/** The day a moment falls on in the person's zone ("2026-10-05"): day dividers, "today". */
export function dayKey(at: string | number | Date): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  const parts = format(
    "key",
    { year: "numeric", month: "2-digit", day: "2-digit" },
    "en-US",
  ).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Today in the person's zone. */
export function today(now: number | Date = Date.now()): string {
  return dayKey(now);
}

/** Today in another zone (the company's: the spend month turns there); the person's when it
 *  isn't one. */
export function todayIn(tz: string | null | undefined, now: number | Date = Date.now()): string {
  if (!tz) return today(now);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(now));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    return today(now);
  }
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A calendar day's noon in UTC (its weekday and month whatever the zone), or null. */
function noonOf(day: string): Date | null {
  const m = DAY.exec(day);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A calendar day `n` days on (back, when negative). */
export function addDays(day: string, n: number): string {
  const d = noonOf(day);
  if (!d) return day;
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whole days from one calendar day to another (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  const a = noonOf(from);
  const b = noonOf(to);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** What the person's zone is ahead of UTC at a moment, in ms. */
function offsetAt(ms: number): number {
  const parts = format(
    "offset",
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    },
    "en-US",
  ).formatToParts(new Date(ms));
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  // Midnight: some engines still write "24" for it.
  const hour = n("hour") % 24;
  const wall = Date.UTC(n("year"), n("month") - 1, n("day"), hour, n("minute"), n("second"));
  return wall - Math.floor(ms / 1000) * 1000;
}

/** A wall-clock time on a calendar day in the person's zone, as an instant ("valid until Fri
 *  Oct 2" = 23:59:59 that day there); null for a malformed day. */
export function zonedTime(day: string, h = 0, m = 0, s = 0): string | null {
  const d = DAY.exec(day);
  if (!d) return null;
  const wall = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), h, m, s);
  // Once, then again at the found moment (a daylight-saving change between the two).
  let at = wall - offsetAt(wall);
  at = wall - offsetAt(at);
  return new Date(at).toISOString();
}

// English short months as en-US writes them: en-GB's (the English interface's day-first
// locale) "Sept" isn't one (R10-B2).
const EN_MONTHS = "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec".split(" ");

const calendars = new Map<string, Intl.DateTimeFormat>();

/** A calendar day written with Intl's `opts` in the interface language's locale (or `loc`),
 *  as its parts: the day as it is, never shifted by a zone; an English short month is "Sep". */
export function formatDayParts(
  day: string,
  opts: Intl.DateTimeFormatOptions,
  loc: string = dateLocale(),
): Intl.DateTimeFormatPart[] {
  const at = noonOf(day);
  if (!at) return [{ type: "literal", value: day }];
  const id = `${loc}|${JSON.stringify(opts)}`;
  let f = calendars.get(id);
  if (!f) {
    f = new Intl.DateTimeFormat(loc, { ...opts, timeZone: "UTC" });
    if (calendars.size > 200) calendars.clear();
    calendars.set(id, f);
  }
  const parts = f.formatToParts(at);
  if (opts.month !== "short" || !loc.startsWith("en")) return parts;
  const month = EN_MONTHS[at.getUTCMonth()] ?? "";
  return parts.map((p) => (p.type === "month" ? { ...p, value: month } : p));
}

/** A calendar day written with Intl's `opts` ("Mon 28 Sep", "28 Sep 2025"): see formatDayParts. */
export function formatDay(
  day: string,
  opts: Intl.DateTimeFormatOptions,
  loc: string = dateLocale(),
): string {
  return formatDayParts(day, opts, loc)
    .map((p) => p.value)
    .join("");
}

/** A day divider's words for a calendar day in the person's zone: "Today · Monday 28 Sep",
 *  "Yesterday · …", "Monday 28 Sep" this year, "28 Sep 2025" before. */
export function dayLabel(day: string, now: number | Date = Date.now()): string {
  const current = today(now);
  const long = formatDay(day, { weekday: "long", day: "numeric", month: "short" });
  if (day === current) return t("chat.today", { date: long });
  if (day === addDays(current, -1)) return t("chat.yesterday", { date: long });
  return day.slice(0, 4) === current.slice(0, 4)
    ? long
    : formatDay(day, { day: "numeric", month: "short", year: "numeric" });
}

/** The calendar day a value names: a calendar day or a wall-clock time without a zone
 *  ("2026-09-28", "2026-09-28T12:00:00", as date fields give them) as it is written; a moment
 *  (an ISO time with its zone, a Date) as the day it falls on in the person's zone. */
export function dayOfValue(value: string | Date): string {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T[\d:.]+)?$/.test(value)) {
    return value.slice(0, 10);
  }
  return dayKey(value);
}
